import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";

const matchesTarget = (ticket, type) => {
    if (!ticket) return false;
    const target = type === "close" ? "CLOSED" : "RESOLVED";
    return ticket.status === target && !!ticket.archivedAt;
};

export default function useTicketQuickAction({ ticket, ticketId, handleUpdate, setTicket, t }) {
    const [pendingUndo, setPendingUndo] = useState(null);

    const quickActionUndoMode = useMemo(() => {
        if (!ticket || !ticketId || !pendingUndo) return null;
        if (pendingUndo.ticketId !== ticketId) return null;
        return matchesTarget(ticket, pendingUndo.type) ? pendingUndo.type : null;
    }, [ticket, ticketId, pendingUndo]);

    const handleQuickAction = useCallback(async (type) => {
        if (!ticketId || !ticket) return;
        const isUndo =
            pendingUndo?.ticketId === ticketId &&
            pendingUndo?.type === type &&
            matchesTarget(ticket, type);

        if (isUndo) {
            const prev = pendingUndo.prev || { status: "OPEN", archived: false };
            const ok = await handleUpdate({ status: prev.status, archived: prev.archived });
            if (!ok) return;
            setTicket((p) => ({
                ...p,
                status: prev.status,
                archivedAt: prev.archived ? (p?.archivedAt || new Date().toISOString()) : null,
            }));
            setPendingUndo(null);
            toast.success(t("admin.support.quickActionUndone", "Reverted to previous state"));
            return;
        }

        const targetStatus = type === "close" ? "CLOSED" : "RESOLVED";
        const prev = { status: ticket.status, archived: !!ticket.archivedAt };
        const ok = await handleUpdate({ status: targetStatus, archived: true });
        if (!ok) return;
        setTicket((p) => ({ ...p, status: targetStatus, archivedAt: new Date().toISOString() }));
        setPendingUndo({ ticketId, type, prev });
        toast.success(
            type === "close"
                ? t("admin.support.ticketClosedAndArchived", "Ticket closed and archived")
                : t("admin.support.ticketResolvedAndArchived", "Ticket resolved and archived"),
        );
    }, [ticketId, ticket, handleUpdate, setTicket, t, pendingUndo]);

    return { quickActionUndoMode, handleQuickAction };
}
